import { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";
import useEvent from "~/hooks/useEvent";
import { fxFlagCookie, parseFxFlagValue } from "~/lib/flags/fxFlagCookie";
import { usePrecomputedFlags } from "~/lib/flags/PrecomputedFlagsContext";
import { useIsomorphicLayoutEffect } from "~/hooks/useIsomorphicLayoutEffect";
import { AppConfig } from "~/utils/AppConfig";

/**
 * How long a variant-gated page waits for Optimizely before falling back to the
 * control experience. The campaign event never arrives when the snippet is
 * blocked or fails to load, so the gate cannot depend on it alone.
 */
export const FLAG_DECISION_FALLBACK_MS = 1500;

const readFlag = (name: string) =>
    typeof window !== "undefined" ? window.featureFlags?.[name] : undefined;

export const useFlag = (name: string) => {
    const [value, setValue] = useState(typeof window !== "undefined" ? readFlag(name) : "");
    const [fxCookieValue, setFxCookieValue] = useState<boolean | string>();

    const handleCampaignDecided = useCallback(() => {
        setValue(readFlag(name));
    }, [name]);

    useEvent("optimizely/campaign/decided", handleCampaignDecided);

    const { flags, flagKeyByVariable, isDecided, exposures } = usePrecomputedFlags();
    const decidedValue = flags[name];
    const flagKey = flagKeyByVariable[name];

    // Reading a decided variable is what exposes the visitor to its experiment.
    useEffect(() => {
        if (decidedValue !== undefined && flagKey) {
            exposures.record(flagKey);
        }
    }, [decidedValue, flagKey, exposures]);

    // A page the proxy decided no flags for (e.g. the Header on /about-us): read
    // the visitor's earlier decision after mount so the first client render
    // still matches the server HTML. A decided page is never overridden by it.
    useEffect(() => {
        if (isDecided) return;

        const cookieValue = parseFxFlagValue(Cookies.get(fxFlagCookie(name)));

        if (cookieValue !== undefined) {
            setFxCookieValue(cookieValue);
        }
    }, [name, isDecided]);

    // On a decided page the server-rendered decision wins.
    return decidedValue ?? fxCookieValue ?? value;
};

type FlagDecision = {
    value: boolean | string | undefined;
    /**
     * `false` until Optimizely has answered for this flag (or the fallback
     * elapsed). A page that renders a different tree per variant must render
     * neither until this is `true`, otherwise the control tree is painted and
     * then replaced.
     */
    isDecided: boolean;
};

/**
 * `useFlag` plus whether the value can be trusted yet.
 *
 * The value is never read during render: the server has no `window`, so a
 * render-time read makes the first client render disagree with the server HTML
 * and hydration discards the variant tree. Resolution runs in a layout effect
 * instead, so a decision that is already available at hydration is applied
 * before the browser paints.
 */
export const useFlagDecision = (name: string): FlagDecision => {
    const [decision, setDecision] = useState<FlagDecision>({
        value: undefined,
        isDecided: false,
    });

    const decideFromCampaign = useCallback(() => {
        setDecision((current) => {
            const value = readFlag(name);

            if (value === undefined && !current.isDecided) {
                return current;
            }

            // Production commits the first defined campaign answer or timeout
            // fallback so a late Optimizely event cannot unmount the painted
            // checkout tree.
            // Local and preview keep applying so `window.featureFlags` +
            // `optimizely/campaign/decided` still toggles variants from the console.
            if (AppConfig.isProduction && current.isDecided) {
                return current;
            }

            return { value, isDecided: true };
        });
    }, [name]);

    const applyTimeoutFallback = useCallback(() => {
        setDecision((current) => {
            if (current.isDecided) {
                return current;
            }

            return { value: undefined, isDecided: true };
        });
    }, []);

    useIsomorphicLayoutEffect(() => {
        // Already-known flags come from a campaign decided before this mounted
        // (client-side navigation, or a snippet that ran before hydration).
        decideFromCampaign();
    }, [decideFromCampaign]);

    useEvent("optimizely/campaign/decided", decideFromCampaign);

    useEffect(() => {
        if (decision.isDecided) return;

        const fallback = setTimeout(applyTimeoutFallback, FLAG_DECISION_FALLBACK_MS);

        return () => clearTimeout(fallback);
    }, [applyTimeoutFallback, decision.isDecided]);

    return decision;
};

export default useFlag;
